import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../../lib/utils';
import { useSidebar } from './sidebar';
import { Badge } from '../badge';

/**
 * SidebarNav components with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Nav item: 8px padding, 6px radius
 * - Active: Accent bg at 10%, left indicator bar
 * - Icon: 18px
 * - Label: 13px/500
 * - Badge: Right aligned, accent color
 */

const navItemVariants = cva(
  [
    'flex items-center gap-2.5 px-2.5 py-2 rounded-md cursor-pointer',
    'text-grey-500 transition-all duration-100 ease-out',
    'hover:bg-grey-100 hover:text-grey-900',
    'dark:text-grey-400 dark:hover:bg-grey-800 dark:hover:text-grey-50',
    'relative mb-0.5',
  ],
  {
    variants: {
      active: {
        true: [
          'bg-teal-500/10 text-teal-500',
          'hover:bg-teal-500/15 hover:text-teal-600',
          'dark:bg-teal-500/10 dark:text-teal-400',
          'dark:hover:bg-teal-500/15 dark:hover:text-teal-300',
        ],
        false: '',
      },
    },
    defaultVariants: {
      active: false,
    },
  }
);

export const SidebarNavItem = React.forwardRef(
  (
    {
      className,
      icon: Icon,
      label,
      badge,
      badgeVariant = 'info',
      beta,
      active,
      onClick,
      ...props
    },
    ref
  ) => {
    const { collapsed } = useSidebar();

    return (
      <div
        ref={ref}
        className={cn(navItemVariants({ active }), className)}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        title={collapsed ? label : undefined}
        {...props}
      >
        {/* Active indicator bar */}
        {active && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-teal-500 rounded-r" />
        )}

        {/* Icon */}
        {Icon && <Icon className="w-[18px] h-[18px] shrink-0" />}

        {/* Label - hidden when collapsed */}
        {!collapsed && (
          <>
            <span className="text-[13px] font-medium whitespace-nowrap flex-1">
              {label}
            </span>

            {/* Badge (dynamic count) */}
            {badge !== undefined && badge !== null && badge > 0 && (
              <Badge
                variant={badgeVariant}
                className="ml-auto text-[11px] px-1.5 py-0"
              >
                {badge > 99 ? '99+' : badge}
              </Badge>
            )}

            {/* Beta tag */}
            {beta && (
              <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-grey-100 text-grey-500 dark:bg-grey-800 dark:text-grey-400">
                Beta
              </span>
            )}
          </>
        )}
      </div>
    );
  }
);
SidebarNavItem.displayName = 'SidebarNavItem';

export const SidebarNavGroup = React.forwardRef(
  ({ className, label, children, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div ref={ref} className={cn('mb-2', className)} {...props}>
        {label && !collapsed && (
          <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-grey-400 dark:text-grey-500">
            {label}
          </div>
        )}
        {children}
      </div>
    );
  }
);
SidebarNavGroup.displayName = 'SidebarNavGroup';
